import { Component, OnDestroy, WritableSignal, signal, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from "@angular/common";

/**
 * A dropdown component that mimics NgbDropdown behavior.
 * Only one dropdown can be open at a time.
 */
@Component({
    selector: 'app-daisy-dropdown',
    standalone: true,
    imports: [CommonModule],
    template: `
        <!-- Dropdown container with relative positioning -->
        <div class="relative inline-block text-left" #dropdownContainer>
            <!-- Dropdown Trigger Button -->
            <div (click)="toggleDropdown($event)">
                <ng-content select="[dropdown-toggle]"></ng-content>
            </div>
            
            <!-- Dropdown Menu Content with proper positioning -->
            <ul *ngIf="isOpen()"
                #dropdownMenu
                [ngStyle]="menuPosition"
                class="absolute bg-white border border-gray-200 rounded-lg py-0 z-50 divide-y divide-gray-100 min-w-[160px]"
                (click)="closeOnItemClick($event)">
                <ng-content select="[dropdown-menu]"></ng-content>
            </ul>
        </div>
    `,
    styles: [`
        :host {
            display: inline-block;
        }
        
        /* Ensure dropdown menu appears above other content */
        :host ::ng-deep ul[dropdown-menu] {
            z-index: 1000;
        }

        /* Remove any unexpected background layering from child link hover states inside this component */
        :host ::ng-deep ul a {
            background: transparent;
        }
    `]
})
export class DaisyDropdownComponent implements OnDestroy, AfterViewInit {
    public isOpen: WritableSignal<boolean> = signal(false);
    public menuPosition: any = {};

    private static openDropdowns: DaisyDropdownComponent[] = [];
    private scrollHandler: (() => void) | null = null;
    private resizeHandler: (() => void) | null = null;
    private positionUpdateFrame: number | null = null;

    constructor(private elementRef: ElementRef) {
        document.addEventListener('click', this.handleGlobalClick);
    }

    ngAfterViewInit(): void {
        // Register this dropdown instance
    }

    ngOnDestroy(): void {
        document.removeEventListener('click', this.handleGlobalClick);
        this.removeScrollAndResizeListeners();
        
        // Cancel any pending animation frame
        if (this.positionUpdateFrame !== null) {
            cancelAnimationFrame(this.positionUpdateFrame);
        }
        
        // Remove from open dropdowns list
        const index = DaisyDropdownComponent.openDropdowns.indexOf(this);
        if (index > -1) {
            DaisyDropdownComponent.openDropdowns.splice(index, 1);
        }
    }

    public toggleDropdown(event?: Event) {
        event?.stopPropagation();

        const wasOpen = this.isOpen();

        // Close all other dropdowns first
        DaisyDropdownComponent.openDropdowns.forEach(dropdown => {
            if (dropdown !== this) {
                dropdown.close();
            }
        });

        // Toggle this dropdown
        if (!wasOpen) {
            this.open();
        } else {
            this.close();
        }
    }

    private open(): void {
        this.isOpen.set(true);

        // Add to open dropdowns list
        if (!DaisyDropdownComponent.openDropdowns.includes(this)) {
            DaisyDropdownComponent.openDropdowns.push(this);
        }

        // Calculate position after opening to ensure menu stays in viewport
        setTimeout(() => {
            this.calculatePosition();
            this.attachScrollAndResizeListeners();
        }, 0);
    }

    public close(): void {
        this.isOpen.set(false);
        this.removeScrollAndResizeListeners();

        // Remove from open dropdowns list
        const index = DaisyDropdownComponent.openDropdowns.indexOf(this);
        if (index > -1) {
            DaisyDropdownComponent.openDropdowns.splice(index, 1);
        }
    }

    private attachScrollAndResizeListeners(): void {
        // Remove existing listeners first
        this.removeScrollAndResizeListeners();

        // Scroll handler with requestAnimationFrame for smooth updates
        this.scrollHandler = () => {
            if (this.positionUpdateFrame !== null) {
                cancelAnimationFrame(this.positionUpdateFrame);
            }
            
            this.positionUpdateFrame = requestAnimationFrame(() => {
                if (this.isOpen()) {
                    this.calculatePosition();
                    this.checkIfTriggerInViewport();
                }
            });
        };

        // Resize handler
        this.resizeHandler = () => {
            if (this.isOpen()) {
                this.calculatePosition();
            }
        };

        // Listen to scroll on window and all scrollable parents
        window.addEventListener('scroll', this.scrollHandler, true); // Use capture phase
        window.addEventListener('resize', this.resizeHandler);
    }

    private removeScrollAndResizeListeners(): void {
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler, true);
            this.scrollHandler = null;
        }

        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }

        if (this.positionUpdateFrame !== null) {
            cancelAnimationFrame(this.positionUpdateFrame);
            this.positionUpdateFrame = null;
        }
    }

    private checkIfTriggerInViewport(): void {
        const container = this.elementRef.nativeElement.querySelector('[class*="relative"]') as HTMLElement;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Close dropdown if trigger is completely out of viewport
        if (rect.bottom < 0 || rect.top > viewportHeight || 
            rect.right < 0 || rect.left > viewportWidth) {
            this.close();
        }
    }

    private calculatePosition(): void {
        const container = this.elementRef.nativeElement.querySelector('[class*="relative"]') as HTMLElement;
        const menu = this.elementRef.nativeElement.querySelector('ul') as HTMLElement;

        if (!container || !menu) return;

        const containerRect = container.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        let top = containerRect.bottom + 8;
        let left: number | undefined;
        let right: number | undefined;
        let openUpward = false;

        // Check if menu goes off bottom edge
        if (containerRect.bottom + menuRect.height + 8 > viewportHeight) {
            // Check if there's more space above
            if (containerRect.top > viewportHeight - containerRect.bottom) {
                openUpward = true;
                top = containerRect.top - menuRect.height - 8;
            }
        }

        // Handle horizontal positioning
        // Try to align to the right edge first
        right = viewportWidth - containerRect.right;

        // Check if menu goes off left edge
        if (containerRect.right - menuRect.width < 0) {
            // Align to left edge instead
            left = containerRect.left;
            right = undefined;
        }

        // Final boundary check - ensure menu stays within viewport
        if (top < 0) {
            top = 8;
        }
        if (top + menuRect.height > viewportHeight) {
            top = viewportHeight - menuRect.height - 8;
        }

        this.menuPosition = {
            position: 'fixed',
            top: top + 'px',
            ...(left !== undefined ? { left: left + 'px' } : { right: right + 'px' })
        };
    }

    // Function to close the dropdown when clicking a menu item
    public closeOnItemClick(event: Event): void {
        const target = event.target as HTMLElement;
        if (target.tagName === 'A' || target.tagName === 'BUTTON' || target.closest('a') || target.closest('button')) {
            this.close();
        }
    }

    // Global click handler to dismiss the dropdown if the click is outside this component
    handleGlobalClick = (event: MouseEvent): void => {
        if (!this.isOpen()) return;

        const target = event.target as HTMLElement;
        if (!this.elementRef.nativeElement.contains(target)) {
            this.close();
        }
    };
}